import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Container,
    Segment,
    Table,
    Grid,
    Header,
    Icon,
    Label,
    Button,
    Message,
    Input,
    Dropdown,
} from "semantic-ui-react";
import { useContext } from "react";
import { UserContext } from "../context/UserContext";
import { db } from "../firebase/firebaseConfig";
import { Link } from "react-router-dom";
import { getDocs, collection, where, query } from "firebase/firestore";
import moment from "moment";

import {
    ConfirmaModal,
    ConfirmaBtn,
    Boton2,
    Overlay,
} from "../elementos/General";

const stickyTh = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#f9f9f9",
};

const ESTADOS = {
    ALL: "ALL",
    VENCIDA: "VENCIDA",
    PROXIMA: "PROXIMA",
    AL_DIA: "AL_DIA",
};

const Mantenimiento = () => {
    const { users } = useContext(UserContext);

    const [mantencion, setMantencion] = useState([]);
    const [status, setStatus] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modal, setModal] = useState(false);
    const [modalST, setModalST] = useState(false);

    // filtros
    const [busqueda, setBusqueda] = useState("");
    const [estadoFiltro, setEstadoFiltro] = useState(ESTADOS.ALL);

    const hoyStart = useMemo(() => moment().startOf("day").valueOf(), []);

    const getMantenimiento = async () => {
        if (!users?.emp_id) return;
        const traer = collection(db, "mantenciones");
        const qMant = query(traer, where("emp_id", "==", users.emp_id));
        const snap = await getDocs(qMant);
        setMantencion(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    };

    const getStatus = async () => {
        if (!users?.emp_id) return;
        const traer = collection(db, "status");
        const qSt = query(traer, where("emp_id", "==", users.emp_id));
        const snap = await getDocs(qSt);
        setStatus(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    };

    useEffect(() => {
        if (!users?.emp_id) return;

        setLoading(true);
        Promise.all([getMantenimiento(), getStatus()])
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users?.emp_id]);

    const formatearFecha = (fecha) => {
        if (!fecha?.toDate) return "-";
        return moment(fecha.toDate()).format("DD/MM/YYYY HH:mm");
    };

    // serie en servicio técnico (Set para lookup O(1))
    const seriesEnST = useMemo(() => {
        const s = new Set();
        status.forEach((st) => {
            if (st?.serie && st?.status === "SERVICIO TECNICO") s.add(st.serie);
        });
        return s;
    }, [status]);

    const estadoMant = useCallback((item) => {
        const finMs = item?.fecha_termino?.toDate?.()
            ? moment(item.fecha_termino.toDate()).startOf("day").valueOf()
            : 0;

        if (finMs <= hoyStart) return { key: ESTADOS.VENCIDA, label: "VENCIDA", color: "red", diasRest: 0 };

        const diasRest = Math.ceil((finMs - hoyStart) / (1000 * 60 * 60 * 24));
        if (diasRest <= 7) return { key: ESTADOS.PROXIMA, label: `PRÓXIMA (${diasRest}d)`, color: "orange", diasRest };

        return { key: ESTADOS.AL_DIA, label: `AL DÍA (${diasRest}d)`, color: "green", diasRest };
    }, [hoyStart]);

    // orden por fecha_termino asc
    const manteOrd = useMemo(() => {
        return [...mantencion].sort((a, b) => {
            const aMs = a?.fecha_termino?.toMillis?.() ?? 0;
            const bMs = b?.fecha_termino?.toMillis?.() ?? 0;
            return aMs - bMs;
        });
    }, [mantencion]);

    // KPIs
    const total = manteOrd.length;
    const vencidas = useMemo(
        () => manteOrd.filter((m) => estadoMant(m).key === ESTADOS.VENCIDA).length,
        [manteOrd, estadoMant]
    );

    const proximas = useMemo(
        () => manteOrd.filter((m) => estadoMant(m).key === ESTADOS.PROXIMA).length,
        [manteOrd, estadoMant]
    );

    const alDia = useMemo(
        () => manteOrd.filter((m) => estadoMant(m).key === ESTADOS.AL_DIA).length,
        [manteOrd, estadoMant]
    );



    const opcionesEstado = [
        { key: ESTADOS.ALL, text: "Todas", value: ESTADOS.ALL, icon: "list" },
        { key: ESTADOS.VENCIDA, text: "Vencidas", value: ESTADOS.VENCIDA, icon: "warning circle" },
        { key: ESTADOS.PROXIMA, text: "Próximas (≤7 días)", value: ESTADOS.PROXIMA, icon: "clock outline" },
        { key: ESTADOS.AL_DIA, text: "Al día (>7 días)", value: ESTADOS.AL_DIA, icon: "check circle" },
    ];

    // filtrado (estado + búsqueda)
    const manteFiltrada = useMemo(() => {
        const q = (busqueda || "").trim().toLowerCase();

        const byEstado = manteOrd.filter((m) => {
            if (estadoFiltro === ESTADOS.ALL) return true;
            return estadoMant(m).key === estadoFiltro;
        });

        if (!q) return byEstado;

        return byEstado.filter((m) => {
            const hay = [
                m.tipo,
                m.serie,
                m.nombre_protocolo,
                m.familia,
                m.programa,
                m.id_eq,
                m.enproceso,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return hay.includes(q);
        });
    }, [manteOrd, busqueda, estadoFiltro,estadoMant]);

    const limpiarFiltros = () => {
        setBusqueda("");
        setEstadoFiltro(ESTADOS.ALL);
    };

    const refrescar = async () => {
        setLoading(true);
        try {
            await Promise.all([getMantenimiento(), getStatus()]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid style={{ maxWidth: 1400 }}>
            {/* CABECERA */}
            <Segment inverted color="teal" tertiary>
                <Header as="h2" icon textAlign="center">
                    <Icon name="wrench" />
                    Mantenciones
                    <Header.Subheader>Ejecutar mantención según fecha y estado del equipo</Header.Subheader>
                </Header>
            </Segment>

            {/* KPIs */}
            <Segment>
                <Grid stackable verticalAlign="middle">
                    <Grid.Row columns={4}>
                        <Grid.Column>
                            <Label color="teal" size="large">
                                Total <Label.Detail>{total}</Label.Detail>
                            </Label>
                        </Grid.Column>
                        <Grid.Column textAlign="center">
                            <Label color="red" size="large">
                                Vencidas <Label.Detail>{vencidas}</Label.Detail>
                            </Label>
                        </Grid.Column>
                        <Grid.Column textAlign="center">
                            <Label color="orange" size="large">
                                Próximas <Label.Detail>{proximas}</Label.Detail>
                            </Label>
                        </Grid.Column>
                        <Grid.Column textAlign="right">
                            <Label color="green" size="large">
                                Al día <Label.Detail>{alDia}</Label.Detail>
                            </Label>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Segment>

            {/* CONTROLES */}
            <Segment>
                <Grid stackable verticalAlign="middle">
                    <Grid.Row columns={4}>
                        <Grid.Column width={7}>
                            <Input
                                fluid
                                icon="search"
                                placeholder="Buscar por serie, equipo, protocolo, programa…"
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                            />
                        </Grid.Column>

                        <Grid.Column width={5}>
                            <Dropdown
                                fluid
                                selection
                                options={opcionesEstado}
                                value={estadoFiltro}
                                onChange={(_, data) => setEstadoFiltro(data.value)}
                                placeholder="Filtrar por estado"
                            />
                        </Grid.Column>

                        <Grid.Column width={2} textAlign="center">
                            <Label color="teal" size="large">
                                Resultados <Label.Detail>{manteFiltrada.length}</Label.Detail>
                            </Label>
                        </Grid.Column>

                        <Grid.Column width={2} textAlign="right">
                            <Button basic icon labelPosition="left" onClick={refrescar} loading={loading}>
                                <Icon name="refresh" />
                                Actualizar
                            </Button>
                            <Button basic icon labelPosition="left" onClick={limpiarFiltros} style={{ marginLeft: 8 }}>
                                <Icon name="eraser" />
                                Limpiar
                            </Button>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Segment>

            {/* TABLA */}
            <Segment raised color="teal" style={{ padding: 0 }}>
                <div style={{ maxHeight: 520, overflow: "auto", padding: 10 }}>
                    <Table celled compact striped selectable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell style={stickyTh}>N°</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh}>Equipo</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh}>Serie</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh}>Protocolo</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh} textAlign="center">Plan</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh}>Fecha Inicio</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh}>Próx. Mantención</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh} textAlign="center">Estado</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh} textAlign="center">Ejecutar</Table.HeaderCell>
                                <Table.HeaderCell style={stickyTh}>Obs</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {loading && (
                                <Table.Row>
                                    <Table.Cell colSpan={10}>
                                        <Message icon info style={{ margin: 0 }}>
                                            <Icon name="spinner" loading />
                                            <Message.Content>
                                                <Message.Header>Cargando…</Message.Header>
                                                Consultando mantenciones y status.
                                            </Message.Content>
                                        </Message>
                                    </Table.Cell>
                                </Table.Row>
                            )}

                            {!loading &&
                                manteFiltrada.map((item, index) => {
                                    const est = estadoMant(item);
                                    const vencida = est.key === ESTADOS.VENCIDA;

                                    // reglas de ejecución
                                    const puedePorFecha = vencida; // tu regla original: solo si vencida
                                    const enST = seriesEnST.has(item.serie);

                                    const ejecutarOk = puedePorFecha && enST;

                                    return (
                                        <Table.Row key={item.id ?? index}>
                                            <Table.Cell>{index + 1}</Table.Cell>

                                            <Table.Cell style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                                                <b>{item.tipo}</b>
                                                {item.familia ? (
                                                    <div style={{ fontSize: 12, opacity: 0.8 }}>Fam: {item.familia}</div>
                                                ) : null}
                                            </Table.Cell>

                                            <Table.Cell>{item.serie}</Table.Cell>

                                            <Table.Cell style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                                                {item.nombre_protocolo}
                                                {item.programa ? (
                                                    <div style={{ fontSize: 12, opacity: 0.8 }}>Prog: {item.programa}</div>
                                                ) : null}
                                            </Table.Cell>

                                            <Table.Cell textAlign="center">
                                                <Label basic size="tiny">{item.dias} días</Label>
                                            </Table.Cell>

                                            <Table.Cell>{formatearFecha(item.fecha_inicio)}</Table.Cell>
                                            <Table.Cell>{formatearFecha(item.fecha_termino)}</Table.Cell>

                                            <Table.Cell textAlign="center">
                                                <Label color={est.color} size="tiny">
                                                    {est.label}
                                                </Label>
                                            </Table.Cell>

                                            <Table.Cell textAlign="center">
                                                {ejecutarOk ? (
                                                    <Button
                                                        as={Link}
                                                        to={`/ejecutarmantencion/${item.id}`}
                                                        size="tiny"
                                                        color="red"
                                                        icon
                                                        labelPosition="left"
                                                        title="Ejecutar Mantención"
                                                    >
                                                        <Icon name="play" />
                                                        Ejecutar
                                                    </Button>
                                                ) : puedePorFecha && !enST ? (
                                                    <Button
                                                        size="tiny"
                                                        basic
                                                        color="orange"
                                                        icon
                                                        labelPosition="left"
                                                        onClick={() => setModalST(true)}
                                                        title="Debe estar en Servicio Técnico"
                                                    >
                                                        <Icon name="warning circle" />
                                                        No en ST
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        size="tiny"
                                                        basic
                                                        color="teal"
                                                        icon
                                                        labelPosition="left"
                                                        onClick={() => setModal(true)}
                                                        title="Aún no corresponde ejecutar"
                                                    >
                                                        <Icon name="clock outline" />
                                                        No disponible
                                                    </Button>
                                                )}
                                            </Table.Cell>

                                            <Table.Cell>
                                                {item.enproceso === "1" ? (
                                                    <Label basic color="orange" size="tiny">Sin Confirmar</Label>
                                                ) : (
                                                    ""
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}

                            {!loading && manteFiltrada.length === 0 && (
                                <Table.Row>
                                    <Table.Cell colSpan={10}>
                                        <Message warning icon style={{ margin: 0 }}>
                                            <Icon name="search" />
                                            <Message.Content>
                                                <Message.Header>Sin resultados</Message.Header>
                                                No hay mantenciones que coincidan con los filtros actuales.
                                            </Message.Content>
                                        </Message>
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </div>
            </Segment>

            {/* MODALES (los tuyos) */}
            {modal && (
                <Overlay>
                    <ConfirmaModal className="confirmation-modal">
                        <h2>¡Este equipo no está disponible para Ejecutar Mantención!</h2>
                        <ConfirmaBtn style={{ justifyContent: "center" }} className="confirmation-buttons">
                            <Boton2 onClick={() => setModal(false)}>Aceptar</Boton2>
                        </ConfirmaBtn>
                    </ConfirmaModal>
                </Overlay>
            )}

            {modalST && (
                <Overlay>
                    <ConfirmaModal className="confirmation-modal">
                        <h2>¡Este equipo no se encuentra en Servicio Técnico!</h2>
                        <ConfirmaBtn style={{ justifyContent: "center" }} className="confirmation-buttons">
                            <Boton2 onClick={() => setModalST(false)}>Aceptar</Boton2>
                        </ConfirmaBtn>
                    </ConfirmaModal>
                </Overlay>
            )}
        </Container>
    );
};

export default Mantenimiento;
